import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatProductPrice } from '../../utils/productPrice';

const POPULAR_IDS = ['chap-5x10', 'pack-40pers', 'guirlande-15m', 'chaise-noire-confort', 'bateau-pirate', 'barbe-papa', 'tente-3x6', 'pack-sono-ambiance', 'table-6-8-70', 'mange-debout'];

const ProductsSection = () => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [popularProducts, setPopularProducts] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
      .then(res => res.json())
      .then(data => {
        const filtered = data.filter(p => POPULAR_IDS.includes(p.id)).slice(0, 10);
        setPopularProducts(filtered);
      })
      .catch(() => {});
  }, []);

  // Auto-scroll every 1.5 seconds
  useEffect(() => {
    if (popularProducts.length === 0) return;
    const interval = setInterval(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (container.scrollLeft >= maxScroll - 10) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: 310, behavior: 'smooth' });
      }
      setTimeout(checkScroll, 400);
    }, 1500);
    return () => clearInterval(interval);
  }, [popularProducts]);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 350;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  return (
    <section className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8 md:mb-12">
          <div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-1 md:mb-2">Produits Populaires</h2>
            <p className="text-sm md:text-base text-gray-600">Notre sélection de matériel le plus demandé</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${canScrollLeft ? 'bg-orange-100 text-orange-500 hover:bg-orange-200' : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${canScrollRight ? 'bg-orange-100 text-orange-500 hover:bg-orange-200' : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <Link to="/catalogue" className="hidden md:inline-flex items-center gap-2 text-orange-500 font-medium hover:text-orange-600">
              Voir tout <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="relative">
          <div 
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {popularProducts.map((product) => (
              <div 
                key={product.id}
                className="flex-shrink-0 w-[280px] sm:w-[300px] bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow group snap-start"
              >
                <div className="relative aspect-[4/3] bg-gray-100">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105" 
                  />
                  {product.badge && (
                    <span className="absolute top-4 right-4 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                      {product.badge}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <span className="text-xs text-orange-500 font-medium uppercase">{product.category.replace('-', ' ')}</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-orange-500">{formatProductPrice(product)}</span>
                    <Link to="/devis" className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors">
                      Devis
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-10 md:hidden">
          <Link to="/catalogue" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-medium rounded-full">
            Voir tout le catalogue <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
